import React, { useMemo } from 'react';
import { useAcademy } from '../context/AcademyContext';
import SafeIcon from '../common/SafeIcon';
import { formatDateRange } from '../utils/campLogic';

export default function SessionScopeSwitcher() {
  const {
    departments,
    sessions,
    runs,
    currentDepartment,
    currentSession,
    currentRun,
    selectRun,
    loadingAcademy,
    isEnabled
  } = useAcademy();

  const groupedSessions = useMemo(() => {
    return departments.map((department) => ({
      department,
      sessions: sessions.filter(
        (session) =>
          String(session?.department_id) === String(department?.id)
      )
    }));
  }, [departments, sessions]);

  const groupedRuns = useMemo(() => {
    return groupedSessions.map(({ department, sessions: items }) => ({
      department,
      runs: items.flatMap((session) =>
        runs
          .filter(
            (run) => String(run?.session_id) === String(session?.id)
          )
          .map((run) => ({ run, session }))
      )
    }));
  }, [groupedSessions, runs]);

  if (loadingAcademy) {
    return (
      <div className="border-b border-white/10 px-4 py-4 text-sm text-white/70">
        <SafeIcon name="Loader" className="mr-2 inline animate-spin" />
        Loading session scope...
      </div>
    );
  }

  return (
    <div className="border-y border-white/10 bg-black/10 p-4">
      <label
        htmlFor="admin-session-scope"
        className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-amber"
      >
        Managing event run
      </label>
      <select
        id="admin-session-scope"
        value={currentRun?.id ?? ''}
        onChange={(event) => {
          const selected = runs.find(
            (run) =>
              String(run?.id) === String(event.target.value)
          );

          if (selected) {
            selectRun(selected);
          }
        }}
        className="w-full cursor-pointer rounded-md border border-white/20 bg-white px-3 py-2 text-sm font-semibold text-dark-green outline-none focus:border-amber focus:ring-2 focus:ring-amber/30"
      >
        <option value="">Choose an event run</option>
        {groupedRuns.map(({ department, runs: items }) => (
          <optgroup key={department.id} label={department.name}>
            {items.map(({ run, session }) => (
              <option key={run.id} value={run.id}>
                {session.title || session.name}
                {' — '}
                {run.title || run.name}
                {!isEnabled(run) ? ' (inactive)' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      {currentRun ? (
        <div className="mt-3 rounded-md bg-white/10 px-3 py-2 text-xs">
          <div className="font-bold text-white">
            {currentDepartment?.name || 'Learning center'}
          </div>
          <div className="mt-1 font-semibold text-amber">
            {currentSession?.title ||
              currentSession?.name ||
              'Selected session'}
          </div>
          <div className="mt-1 font-semibold text-white">
            {currentRun?.title ||
              currentRun?.name ||
              'Selected event run'}
          </div>
          <div className="mt-1 text-white/70">
            {formatDateRange(currentRun?.start_date, currentRun?.end_date)}
          </div>
          <div className="mt-2 border-t border-white/10 pt-2 text-[10px] uppercase tracking-wide text-white/60">
            Content, podcasts, letters and lab partners are filtered to
            this event run
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-md border border-amber/40 bg-amber/10 px-3 py-2 text-xs text-amber">
          Choose an event run before managing session content.
        </div>
      )}
    </div>
  );
}